import React, { useMemo, useState } from 'react';
import { NavLink, useLocation, useNavigate } from 'react-router-dom';
import { BsBoxArrowRight, BsMoonFill, BsPerson, BsSunFill } from 'react-icons/bs';
import { ChevronDown, LayoutGrid, Menu, Search, X } from 'lucide-react';
import { useAuthStore } from '../../../store/authStore';
import { authService } from '../../../services/authService';
import { useTheme } from '../../../store/themeStore';
import { toCapitalizedWords } from '../../../utils/helpers';
import {
  Popover,
  PopoverContent,
  PopoverTrigger,
} from '../../../components/ui/popover';
import {
  Tooltip,
  TooltipContent,
  TooltipTrigger,
} from '../../../components/ui/tooltip';
import { getUserInitials, getVisibleMerchantNav } from './merchantNav';
import brandLogo from '../../../assets/logo.png';
import './MerchantDashboard.css';

type MerchantDashboardLayoutProps = {
  children: React.ReactNode;
};

function formatUserRole(user: { adminType?: string; role?: string } | null): string {
  if (!user) return 'Merchant';
  const titled = toCapitalizedWords(user.adminType || user.role, 'Merchant');
  return titled.charAt(0) + titled.slice(1).toLowerCase();
}

const MerchantDashboardLayout: React.FC<MerchantDashboardLayoutProps> = ({ children }) => {
  const { user, logout } = useAuthStore();
  const { theme, toggleTheme } = useTheme();
  const navigate = useNavigate();
  const location = useLocation();
  const [profileMenuOpen, setProfileMenuOpen] = useState(false);
  const [launcherOpen, setLauncherOpen] = useState(false);
  const [mobileNavOpen, setMobileNavOpen] = useState(false);
  const [launcherQuery, setLauncherQuery] = useState('');
  const [headerQuery, setHeaderQuery] = useState('');

  const navItems = useMemo(() => getVisibleMerchantNav(user), [user]);
  const userInitials = getUserInitials(user?.fullName);
  const displayName = toCapitalizedWords(user?.fullName, 'User');
  const userRole = formatUserRole(user);
  const canViewProfile = navItems.some((item) => item.path === '/dashboard/profile');

  const filteredNav = useMemo(() => {
    const q = launcherQuery.trim().toLowerCase();
    if (!q) return navItems;
    return navItems.filter(
      (item) =>
        item.label.toLowerCase().includes(q) ||
        item.description.toLowerCase().includes(q) ||
        item.group.toLowerCase().includes(q),
    );
  }, [navItems, launcherQuery]);

  const headerMatches = useMemo(() => {
    const q = headerQuery.trim().toLowerCase();
    if (!q) return navItems;
    return navItems.filter(
      (item) =>
        item.label.toLowerCase().includes(q) ||
        item.description.toLowerCase().includes(q) ||
        item.group.toLowerCase().includes(q),
    );
  }, [navItems, headerQuery]);

  const launcherGroups = useMemo(() => {
    const groups = new Map<string, typeof filteredNav>();
    filteredNav.forEach((item) => {
      const list = groups.get(item.group) ?? [];
      list.push(item);
      groups.set(item.group, list);
    });
    return Array.from(groups.entries());
  }, [filteredNav]);

  const handleLogout = async () => {
    setProfileMenuOpen(false);
    try {
      await authService.logout();
    } catch (error) {
      console.error('Logout failed:', error);
    } finally {
      logout();
      navigate('/login');
    }
  };

  const handleProfileMenuNavigate = (path: string) => {
    setProfileMenuOpen(false);
    setMobileNavOpen(false);
    navigate(path);
  };

  const goTo = (path: string) => {
    setLauncherOpen(false);
    setMobileNavOpen(false);
    setLauncherQuery('');
    setHeaderQuery('');
    navigate(path);
  };

  return (
    <div className="sf-shell sf-shell--lightning">
      <header className="sf-header">
        <div className="sf-header-left">
          <button
            type="button"
            className="sf-icon-btn sf-mobile-nav-btn"
            aria-label="Open navigation"
            onClick={() => setMobileNavOpen((open) => !open)}
          >
            {mobileNavOpen ? <X size={16} /> : <Menu size={16} />}
          </button>
          <Popover
            open={launcherOpen}
            onOpenChange={(open) => {
              setLauncherOpen(open);
              if (!open) setLauncherQuery('');
            }}
          >
            <PopoverTrigger asChild>
              <button type="button" className="sf-waffle" aria-label="App launcher">
                <LayoutGrid size={18} strokeWidth={1.8} />
              </button>
            </PopoverTrigger>
            <PopoverContent className="sf-launcher w-[min(26.25rem,calc(100vw-1.5rem))] p-3.5" align="start" sideOffset={10}>
              <div className="sf-launcher-title">App Launcher</div>
              <div className="sf-launcher-search">
                <Search size={14} />
                <input
                  value={launcherQuery}
                  onChange={(event) => setLauncherQuery(event.target.value)}
                  placeholder="Search apps and items..."
                  aria-label="Search apps"
                />
              </div>
              {launcherGroups.length === 0 ? (
                <div className="sf-launcher-empty">No matching items</div>
              ) : (
                launcherGroups.map(([group, items]) => (
                  <div key={group} className="sf-launcher-group">
                    <div className="sf-launcher-group-title">{group}</div>
                    <div className="sf-launcher-grid">
                      {items.map((item) => {
                        const Icon = item.icon;
                        return (
                          <button
                            key={item.path}
                            type="button"
                            className="sf-launcher-tile"
                            onClick={() => goTo(item.path)}
                          >
                            <span className="sf-launcher-tile-icon">
                              <Icon size={18} strokeWidth={1.75} />
                            </span>
                            <span className="sf-launcher-tile-label">{item.label}</span>
                          </button>
                        );
                      })}
                    </div>
                  </div>
                ))
              )}
            </PopoverContent>
          </Popover>
          <div className="sf-brand">
            <img src={brandLogo} alt="money eu" className="sf-brand-logo" />
            <span className="sf-app-name">Merchant Console</span>
          </div>
        </div>

        <form
          className="sf-global-search"
          onSubmit={(event) => {
            event.preventDefault();
            if (headerMatches[0]) goTo(headerMatches[0].path);
          }}
        >
          <Search size={14} />
          <input
            value={headerQuery}
            onChange={(event) => setHeaderQuery(event.target.value)}
            placeholder="Search this app..."
            aria-label="Search this app"
          />
          {headerQuery.trim() && (
            <div className="sf-search-results">
              {headerMatches.length === 0 ? (
                <div className="sf-launcher-empty">No matching items</div>
              ) : (
                headerMatches.map((item) => (
                  <button
                    key={item.path}
                    type="button"
                    className="sf-search-result"
                    onClick={() => goTo(item.path)}
                  >
                    {item.label}
                    <span>{item.description}</span>
                  </button>
                ))
              )}
            </div>
          )}
        </form>

        <div className="sf-header-right">
          <div className="sf-theme-toggle">
            <Tooltip>
              <TooltipTrigger asChild>
                <button
                  type="button"
                  className={`sf-icon-btn ${theme === 'light' ? 'is-active' : ''}`}
                  onClick={() => theme !== 'light' && toggleTheme()}
                  aria-label="Switch to light mode"
                  aria-pressed={theme === 'light'}
                >
                  <BsSunFill />
                </button>
              </TooltipTrigger>
              <TooltipContent side="bottom">
                {theme === 'light' ? 'Light mode active' : 'Switch to light mode'}
              </TooltipContent>
            </Tooltip>
            <Tooltip>
              <TooltipTrigger asChild>
                <button
                  type="button"
                  className={`sf-icon-btn ${theme === 'dark' ? 'is-active' : ''}`}
                  onClick={() => theme !== 'dark' && toggleTheme()}
                  aria-label="Switch to dark mode"
                  aria-pressed={theme === 'dark'}
                >
                  <BsMoonFill />
                </button>
              </TooltipTrigger>
              <TooltipContent side="bottom">
                {theme === 'dark' ? 'Dark mode active' : 'Switch to dark mode'}
              </TooltipContent>
            </Tooltip>
          </div>
          <Popover open={profileMenuOpen} onOpenChange={setProfileMenuOpen}>
            <PopoverTrigger asChild>
              <button type="button" className="sf-user-chip" aria-label="Account menu">
                <span className="sf-user-avatar">{userInitials}</span>
                <span className="sf-user-meta">
                  <span className="sf-user-name">{displayName}</span>
                  <span className="sf-user-role">{userRole}</span>
                </span>
                <ChevronDown size={14} strokeWidth={1.8} />
              </button>
            </PopoverTrigger>
            <PopoverContent className="dh-profile-menu" align="end" side="bottom" sideOffset={8}>
              <div className="dh-profile-menu-hero">
                <div className="dh-profile-menu-hero-glow" aria-hidden />
                <div className="dh-profile-menu-identity">
                  <span className="dh-profile-menu-avatar">{userInitials}</span>
                  <div className="dh-profile-menu-meta">
                    <div className="dh-profile-menu-name">{displayName}</div>
                    <div className="dh-profile-menu-email">{user?.email || '—'}</div>
                    <span className="dh-profile-menu-role">{userRole}</span>
                  </div>
                </div>
              </div>
              <div className="dh-profile-menu-body">
                {canViewProfile && (
                  <div className="dh-profile-menu-list">
                    <button
                      type="button"
                      className="dh-profile-menu-link"
                      onClick={() => handleProfileMenuNavigate('/dashboard/profile')}
                    >
                      <span className="dh-profile-menu-link-icon-wrap">
                        <BsPerson className="dh-profile-menu-link-icon" />
                      </span>
                      <span className="dh-profile-menu-link-text">
                        <span className="dh-profile-menu-link-label">My Profile</span>
                        <span className="dh-profile-menu-link-hint">Account Settings</span>
                      </span>
                    </button>
                  </div>
                )}
                <button
                  type="button"
                  className="dh-profile-menu-link dh-profile-menu-signout"
                  onClick={handleLogout}
                >
                  <span className="dh-profile-menu-link-icon-wrap">
                    <BsBoxArrowRight className="dh-profile-menu-link-icon" />
                  </span>
                  <span className="dh-profile-menu-link-text">
                    <span className="dh-profile-menu-link-label">Sign Out</span>
                    <span className="dh-profile-menu-link-hint">End This Session</span>
                  </span>
                </button>
              </div>
            </PopoverContent>
          </Popover>
        </div>
      </header>

      <nav className={`sf-tabs ${mobileNavOpen ? 'is-open' : ''}`} aria-label="Merchant navigation">
        {navItems.map((item) => (
          <NavLink
            key={item.path}
            to={item.path}
            end={item.path === '/dashboard/transaction-dashboard'}
            className={({ isActive }) => `sf-tab ${isActive ? 'is-active' : ''}`}
            onClick={() => setMobileNavOpen(false)}
          >
            {item.label}
          </NavLink>
        ))}
      </nav>

      <div className="sf-workspace">
        <div key={location.pathname} className="dashboard-page-enter">
          {children}
        </div>
      </div>
    </div>
  );
};

export default MerchantDashboardLayout;
